import { faBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { faBox } from '@fortawesome/pro-solid-svg-icons/faBox';
import { faBuilding } from '@fortawesome/pro-solid-svg-icons/faBuilding';
import { faBullseye } from '@fortawesome/pro-solid-svg-icons/faBullseye';
import { faChartBar } from '@fortawesome/pro-solid-svg-icons/faChartBar';
import { faChartLine } from '@fortawesome/pro-solid-svg-icons/faChartLine';
import { faClockRotateLeft } from '@fortawesome/pro-solid-svg-icons/faClockRotateLeft';
import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog';
import { faDatabase } from '@fortawesome/pro-solid-svg-icons/faDatabase';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { faFlask } from '@fortawesome/pro-solid-svg-icons/faFlask';
import { faHome } from '@fortawesome/pro-solid-svg-icons/faHome';
import { faKey } from '@fortawesome/pro-solid-svg-icons/faKey';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faMap } from '@fortawesome/pro-solid-svg-icons/faMap';
import { faPlug } from '@fortawesome/pro-solid-svg-icons/faPlug';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { faShield } from '@fortawesome/pro-solid-svg-icons/faShield';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faUserGear } from '@fortawesome/pro-solid-svg-icons/faUserGear';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { NavigationConfig } from './types';
import { getNewAdminUrl } from '../../../utils/urls';
import { AccessRequirement } from '../../../utils/rbac';
import type { UserType } from '../../../../types';

interface V2NavConfigOptions {
  tenantId: string;
  user?: UserType | null;
  flags?: {
    enableMadapi?: boolean;
    tenantProductsManagement?: boolean;
    intentWidgetAndSettingsPage?: boolean;
  };
}

/** Items only org admins (+ SuperKudu / v1) see. */
const ADMIN_ONLY: AccessRequirement = { requiresRole: 'orgAdmin' };
/** Copilot config: org admins of orgs with the Sales Copilot module. */
const COPILOT_ADMIN: AccessRequirement = {
  requiresRole: 'orgAdmin',
  requiresModuleAny: ['sales_copilot'],
};

/**
 * v2 RBAC sidebar config. Mirrors mk-admin-app's `useV2NavConfig` from
 * `src/common/components/layout/app-sidebar/v2-nav-config.ts` (Iago's
 * RGI-1246 follow-ups). Hrefs that point at routes mk-admin-app owns are
 * marked `isExternal: true` and routed there via `getNewAdminUrl()`; the
 * rest stay internal to mk-addax.
 *
 * Access is gated declaratively (`requires` for role/module, `requiresTier`
 * for tier) and applied in `permission-filter.ts`, so the non-admin sidebar
 * stays in lockstep with mk-admin-app. SuperKudu sees the SUPERKUDU ADMIN
 * section.
 */
export function getV2NavigationConfig({
  tenantId,
  user,
  flags = {},
}: V2NavConfigOptions): NavigationConfig {
  const newAdminUrl = getNewAdminUrl();
  const isSuperKudu = !!user?.superKudu;
  const showMadapi = !!flags.enableMadapi || isSuperKudu;

  return {
    sections: [
      {
        items: [
          {
            label: 'Home',
            href: `/org/${tenantId}`,
            icon: faHome,
            activeMatcher: (path: string) => /^\/org\/\d+\/?$/.test(path),
          },
        ],
      },
      {
        label: 'SEGMENTATION',
        items: [
          {
            label: 'Predictions',
            href: `/org/${tenantId}/predictions`,
            icon: faChartBar,
            activeMatcher: (path: string) =>
              path.startsWith(`/org/${tenantId}/predictions`),
          },
          {
            label: 'Score Lookup',
            href: `/org/${tenantId}/profiles`,
            icon: faSearch,
            activeMatcher: (path: string) =>
              path.startsWith(`/org/${tenantId}/profiles`),
          },
        ],
      },
      {
        label: 'USER SETTINGS',
        items: [
          {
            label: 'My Profile',
            href: `${newAdminUrl}/org/${tenantId}/settings/profile`,
            icon: faUser,
            isExternal: true,
            activeMatcher: (path: string) =>
              path.startsWith(`/org/${tenantId}/settings/profile`),
          },
          ...(showMadapi
            ? [
                {
                  label: 'My Mad API Keys',
                  href: `${newAdminUrl}/org/${tenantId}/settings/madapi-keys`,
                  icon: faKey,
                  isExternal: true,
                  activeMatcher: (path: string) =>
                    path === `/org/${tenantId}/settings/madapi-keys`,
                },
              ]
            : []),
        ],
      },
      {
        label: 'COMPANY SETTINGS',
        items: [
          {
            label: 'Account',
            href: `${newAdminUrl}/org/${tenantId}/settings/account`,
            icon: faBuilding,
            isExternal: true,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/settings/account`,
          },
          {
            label: 'Authentication',
            href: `${newAdminUrl}/org/${tenantId}/settings/authentication`,
            icon: faLock,
            isExternal: true,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/settings/authentication`,
          },
          {
            label: 'Audit Trail',
            href: `${newAdminUrl}/org/${tenantId}/settings/audit-trail`,
            icon: faClockRotateLeft,
            requiresTier: 'growth',
            isExternal: true,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/settings/audit-trail`,
          },
          {
            label: 'Users & Teams',
            href: `${newAdminUrl}/org/${tenantId}/settings/users`,
            icon: faUsers,
            isExternal: true,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/settings/users`,
          },
          {
            label: 'Credit Usage',
            href: `${newAdminUrl}/org/${tenantId}/settings/usage`,
            icon: faChartLine,
            isExternal: true,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/settings/usage`,
          },
          {
            label: 'API Keys',
            href: `${newAdminUrl}/org/${tenantId}/settings/api-keys`,
            icon: faKey,
            isExternal: true,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/settings/api-keys`,
          },
          ...(showMadapi
            ? [
                {
                  label: 'MadAPI Usage',
                  href: `${newAdminUrl}/org/${tenantId}/settings/madapi-usage`,
                  icon: faChartBar,
                  isExternal: true,
                  requires: ADMIN_ONLY,
                  activeMatcher: (path: string) =>
                    path === `/org/${tenantId}/settings/madapi-usage`,
                },
              ]
            : []),
          {
            label: 'Privacy',
            href: `/org/${tenantId}/settings/privacy`,
            icon: faShield,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/settings/privacy`,
          },
        ],
      },
      {
        label: 'COPILOT SETTINGS',
        items: [
          {
            label: 'Personas',
            href: `${newAdminUrl}/org/${tenantId}/preferences/persona`,
            icon: faUsers,
            isExternal: true,
            requires: COPILOT_ADMIN,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/preferences/persona`,
          },
          {
            label: 'Ideal Customer Profile',
            href: `${newAdminUrl}/org/${tenantId}/preferences/icp`,
            icon: faChartLine,
            isExternal: true,
            requires: COPILOT_ADMIN,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/preferences/icp`,
          },
          {
            label: 'Profiles to Exclude',
            href: `${newAdminUrl}/org/${tenantId}/preferences/anti-icp`,
            icon: faChartBar,
            isExternal: true,
            requires: COPILOT_ADMIN,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/preferences/anti-icp`,
          },
          ...(flags.tenantProductsManagement
            ? [
                {
                  label: 'Products',
                  href: `${newAdminUrl}/org/${tenantId}/preferences/products`,
                  icon: faBox,
                  isExternal: true,
                  requires: COPILOT_ADMIN,
                  activeMatcher: (path: string) =>
                    path === `/org/${tenantId}/preferences/products`,
                },
              ]
            : []),
          ...(flags.intentWidgetAndSettingsPage
            ? [
                {
                  label: 'Intent Topics',
                  href: `${newAdminUrl}/org/${tenantId}/preferences/intent-topics`,
                  icon: faBullseye,
                  isExternal: true,
                  requires: COPILOT_ADMIN,
                  activeMatcher: (path: string) =>
                    path === `/org/${tenantId}/preferences/intent-topics`,
                },
              ]
            : []),
          {
            label: 'Competitors & Partners',
            href: `${newAdminUrl}/org/${tenantId}/preferences/competitors-partners`,
            icon: faBuilding,
            isExternal: true,
            requires: COPILOT_ADMIN,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/preferences/competitors-partners`,
          },
          {
            label: 'Key Website Pages',
            href: `${newAdminUrl}/org/${tenantId}/preferences/key-website-pages`,
            icon: faCog,
            isExternal: true,
            requires: COPILOT_ADMIN,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/preferences/key-website-pages`,
          },
        ],
      },
      {
        label: 'INTEGRATION SETTINGS',
        items: [
          {
            label: 'Integrations',
            href: `/org/${tenantId}/integrations`,
            icon: faPlug,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path.startsWith(`/org/${tenantId}/integrations`),
          },
          {
            label: 'Mapping',
            href: `/org/${tenantId}/mapping`,
            icon: faMap,
            activeMatcher: (path: string) =>
              path.includes(`/org/${tenantId}/mapping`),
          },
          {
            label: 'Data',
            href: `/org/${tenantId}/data`,
            icon: faDatabase,
            activeMatcher: (path: string) =>
              path.startsWith(`/org/${tenantId}/data`),
          },
          {
            label: 'Processes',
            href: `${newAdminUrl}/org/${tenantId}/settings/processes`,
            icon: faCog,
            isExternal: true,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path === `/org/${tenantId}/settings/processes`,
          },
          {
            label: 'Push Configurations',
            href: `/org/${tenantId}/push`,
            icon: faBell,
            requires: ADMIN_ONLY,
            activeMatcher: (path: string) =>
              path.startsWith(`/org/${tenantId}/push`) ||
              path.startsWith(`/org/${tenantId}/settings/push`),
          },
        ],
      },
      ...(isSuperKudu
        ? [
            {
              label: 'SUPERKUDU ADMIN',
              items: [
                {
                  label: 'Tenant Entitlements',
                  href: `/org/${tenantId}/settings/admin`,
                  icon: faUserGear,
                  activeMatcher: (path: string) =>
                    path === `/org/${tenantId}/settings/admin`,
                },
                {
                  label: 'Audit Trail',
                  href: `${newAdminUrl}/org/${tenantId}/settings/audit-trail`,
                  icon: faClockRotateLeft,
                  isExternal: true,
                  activeMatcher: (path: string) =>
                    path === `/org/${tenantId}/settings/audit-trail`,
                },
                {
                  label: 'Flags',
                  href: `/org/${tenantId}/settings/flags`,
                  icon: faFlag,
                  activeMatcher: (path: string) =>
                    path.startsWith(`/org/${tenantId}/settings/flags`),
                },
                {
                  label: 'YAML to SQL',
                  href: `${newAdminUrl}/org/${tenantId}/settings/yaml-to-sql`,
                  icon: faFlask,
                  isExternal: true,
                  activeMatcher: (path: string) =>
                    path === `/org/${tenantId}/settings/yaml-to-sql`,
                },
              ],
            },
          ]
        : []),
    ],
    bottomLinks: [
      {
        label: 'Help & Support',
        href: `/org/${tenantId}/help`,
        icon: faQuestionCircle,
      },
      ...(isSuperKudu
        ? [
            {
              label: 'SuperKudu Admin',
              href: `${newAdminUrl}/superkudu/onboardings`,
              icon: faShield,
              isExternal: true,
              description: 'SuperKudu administration panel',
              visibleFor: ['superKudu'] as Array<'admin' | 'superKudu'>,
            },
          ]
        : []),
    ],
  };
}
